import { useQueryClient } from '@tanstack/react-query';
import useHandleAppException, {
  AppExceptionResponseData,
} from '../../../../../hooks/useHandleAppException';
import LangEnum from '../../../../../lang/LangEnum';
import { getCatalogProductsPageKeyPrefix } from '../../../../../services/modules/catalogProducts/catalogProducts.keys';
import { removeCatalogProducts } from '../../../../../services/modules/catalogProducts/catalogProducts.repository';
import { getGetCatalogSectionsKey } from '../../../../../services/modules/catalogs/catalogs.keys';
import useIsAdminSeller from '../../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import snackbarStore from '../../../../../stores/Snackbar.store';
import { useApplicationContext } from '../../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../../translation/TranslationContext';
import { catalogsRepository } from '../../../repositories/catalogs.repository';
import { formatFilterIdsSearchParams } from '../../catalogFilters.utils';
import { useCatalogView } from '../contexts/CatalogView.context';
import { useCatalogGroupParams } from './useCatalogGroupParams';
import { useCatalogListContextParams } from './useCatalogListContextParams';
import { useCatalogSortParams } from './useCatalogSortParams';
import { useCatalogUrlFilters } from './useCatalogUrlFilters';

interface UseCatalogMassActionOperationsParams {
  catalogId: string;
  canPersist: boolean;
}

interface MoveToSectionParams {
  itemIds: string[];
  sectionId?: string;
  sectionName?: string;
  isDynamic: boolean;
}

export const useCatalogMassActionOperations = ({
  catalogId,
  canPersist,
}: UseCatalogMassActionOperationsParams) => {
  const { team } = useApplicationContext();
  const isAdminSeller = useIsAdminSeller();
  const queryClient = useQueryClient();
  const { translate } = useTranslation();
  const { handleError } = useHandleAppException();
  const contextParams = useCatalogListContextParams();
  const { gridDataInclude } = useCatalogView();
  const { sortParams } = useCatalogSortParams({ catalogId, canPersist });
  const { groupParams } = useCatalogGroupParams({ catalogId, canPersist });
  const { filters, customerIds } = useCatalogUrlFilters();

  const reload = () => {
    const pageKeyPrefix = [
      team?.id,
      ...getCatalogProductsPageKeyPrefix({
        catalogId,
        contextParams,
        sortParams,
        groupParams,
        filters,
        customerIds,
        gridDataInclude,
      }),
    ];
    const sectionsKey = [
      team?.id,
      ...getGetCatalogSectionsKey({
        id: catalogId,
        filters,
        customerIds,
        contextParams,
        groupParams,
      }),
    ];

    queryClient.invalidateQueries({ queryKey: pageKeyPrefix.slice(0, -1) });
    queryClient.invalidateQueries({ queryKey: sectionsKey });
  };

  const remove = async (itemIds: string[]): Promise<boolean> => {
    const response = await removeCatalogProducts({ catalogId, itemIds, isAdminSeller });

    if (!response.ok) {
      const message = response.data ? handleError(response.data) : undefined;
      snackbarStore.handleMessage({
        type: 'error',
        message: message || translate(LangEnum.CATALOGS_MASS_ACTIONS_REMOVE_ERROR),
      });
      return false;
    }

    snackbarStore.handleMessage({
      type: 'success',
      message: translate(LangEnum.CATALOGS_MASS_ACTIONS_REMOVE_SUCCESS),
    });
    reload();
    return true;
  };

  const moveToSection = async ({
    itemIds,
    sectionId,
    sectionName,
    isDynamic,
  }: MoveToSectionParams): Promise<boolean> => {
    const isMassActionByFilter = itemIds.length === 0;
    const items = itemIds.map((id) => ({ id, sectionId }));
    const response = await catalogsRepository(catalogId).moveProductsToSection(
      { items, isDynamic, sectionId, sectionName },
      {
        searchParams: formatFilterIdsSearchParams(filters, customerIds),
        isAdminSeller: !!isAdminSeller,
        limit: 40,
        skip: 0,
      },
    );

    if (!response.ok) {
      const message = response.data
        ? handleError(response.data as AppExceptionResponseData)
        : undefined;
      snackbarStore.handleMessage({
        type: 'error',
        message: message || translate(LangEnum.CARALOGS_MOVE_TO_SECTION_MODAL_MOVE_PRODUCTS_ERROR),
      });
      return false;
    }

    snackbarStore.handleMessage({
      type: 'success',
      message: translate(
        isMassActionByFilter
          ? LangEnum.CATALOG_MASS_ACTIONS_SUCCESS
          : LangEnum.CARALOGS_MOVE_TO_SECTION_MODAL_MOVE_PRODUCTS_SUCCESS,
      ),
    });
    reload();
    return true;
  };

  return { remove, moveToSection };
};
